/**
 * Cash Management — Branch cash-drawer control shell.
 *
 * UI SHELL ONLY (2026-02). Introduces the tabbed container so future
 * Branch Cash Management increments (Transfers · Shift Handover ·
 * Reconciliation · Analytics) can slot in without redesigning
 * navigation. Consumes ONLY the existing authoritative endpoints
 * shipped in Phase 1 / Phase 2A / Increments 1 & 2:
 *
 *   • GET  /api/reports/eod/cash-position?date=&branch_id=
 *   • GET  /api/reports/eod?date=&branch_id=
 *   • POST /api/reports/eod/cash-position/outflow  (petty_cash · bank_deposit)
 *   • PUT  /api/reports/eod/cash-position/actual-cash
 *
 * NO new endpoints, NO new calculations, NO new RBAC surface.
 *
 * Active tabs today:
 *   [ Overview ]  [ Petty Cash ]  [ Cash Count ]  [ Bank Deposits ]
 *
 * Reserved / parked (disabled · placeholder only, no functionality):
 *   [ Cash Transfers ]  [ Shift Handover ]  [ Reconciliation ]  [ Analytics ]
 *
 * Cash-flow hierarchy the Overview snapshot renders:
 *   Opening → Cash Collection → Less Petty → Expected Closing →
 *   Actual Cash Count → Variance → Bank Deposit → Cash Remaining
 *
 * Business rules preserved (see PRD §Cash Management RBAC Guardrail):
 *   • Bank Deposit does NOT alter Expected Closing.
 *   • Cash Remaining = Actual − Bank Deposit ONLY when Actual exists.
 *   • Bank Deposit ≠ Cash Withdrawal ≠ Petty Cash ≠ Cash Transfer ≠
 *     Accounting Adjustment.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Wallet, Coins, CalculatorIcon as Calculator, Landmark,
  Users, ClipboardCheck, LineChart,
  CalendarDays, Building2, Loader2,
  CheckCircle2, AlertTriangle, RotateCcw, X,
} from 'lucide-react';
import { useAuth } from '../../AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Roles that see this shell — mirrors backend EOD_ROLES. Server is
// authoritative; this is a UX gate only.
const CASH_ROLES = ['clinic_owner', 'super_admin', 'founder', 'accounts', 'front_desk'];

const fmtINR = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

// -------- Sub-tab registry -----------------------------------------
// Reserved future tabs (Cash Transfers · Shift Handover · Reconciliation ·
// Analytics) are rendered as disabled placeholders so the navigation
// architecture is committed today. They render NO functionality.
const SUB_TABS = [
  { id: 'overview',      label: 'Overview',       Icon: Wallet,          active: true  },
  { id: 'petty',         label: 'Petty Cash',     Icon: Coins,           active: true  },
  { id: 'count',         label: 'Cash Count',     Icon: Calculator,      active: true  },
  { id: 'deposits',      label: 'Bank Deposits',  Icon: Landmark,        active: true  },
  { id: 'reconciliation',label: 'Reconciliation', Icon: ClipboardCheck,  active: true  },
  { id: 'handover',      label: 'Shift Handover', Icon: Users,           active: false },
  { id: 'analytics',     label: 'Analytics',      Icon: LineChart,       active: false },
];

export default function CashManagementView() {
  const { user } = useAuth();
  const isAllowed = CASH_ROLES.includes(user?.role);

  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [subTab, setSubTab] = useState('overview');

  // Authoritative snapshots — populated from existing endpoints.
  const [position, setPosition] = useState(null);   // /reports/eod/cash-position
  const [eod, setEod] = useState(null);             // /reports/eod (for cash_collected + full cash_handover)
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const effectiveBranchId = useMemo(() => {
    if (branchId) return branchId;
    if (branches.length === 1) return branches[0]?.branch_id || '';
    return '';
  }, [branchId, branches]);

  useEffect(() => {
    if (!isAllowed) return;
    axios.get(`${API}/branches`)
      .then((r) => setBranches(Array.isArray(r.data) ? r.data : []))
      .catch(() => setBranches([]));
  }, [isAllowed]);

  const load = useCallback(async () => {
    if (!isAllowed) return;
    if (!effectiveBranchId) {
      setPosition(null);
      setEod(null);
      return;
    }
    setLoading(true);
    setErr('');
    try {
      const [posR, eodR] = await Promise.all([
        axios.get(`${API}/reports/eod/cash-position`, {
          params: { date, branch_id: effectiveBranchId },
        }),
        axios.get(`${API}/reports/eod`, {
          params: { date, branch_id: effectiveBranchId },
        }),
      ]);
      setPosition(posR.data);
      setEod(eodR.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load cash management snapshot');
    } finally {
      setLoading(false);
    }
  }, [date, effectiveBranchId, isAllowed]);

  useEffect(() => { load(); }, [load]);

  if (!isAllowed) return null;

  return (
    <div className="space-y-3" data-testid="cash-management-view">
      {/* Branch + date context — cash-management is per (branch, date) */}
      <div
        className="bg-white border border-slate-200 rounded-lg p-3 flex items-center gap-3 flex-wrap"
        data-testid="cash-mgmt-context-bar"
      >
        <div className="flex items-center gap-1.5 text-slate-700">
          <Wallet size={14} className="text-emerald-600" />
          <span className="text-xs font-bold tracking-wide uppercase">Branch Cash Management</span>
        </div>
        <div className="flex-1" />
        <label className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-600">
          <CalendarDays size={12} className="text-slate-400" />
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="cash-mgmt-date"
            className="text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:border-emerald-400"
          />
        </label>
        <label className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-600">
          <Building2 size={12} className="text-slate-400" />
          <select
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            data-testid="cash-mgmt-branch"
            className="text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:border-emerald-400"
          >
            <option value="">
              {branches.length > 1 ? 'Select branch…' : 'All / default'}
            </option>
            {branches.map((b) => (
              <option key={b.branch_id} value={b.branch_id}>{b.name}</option>
            ))}
          </select>
        </label>
      </div>

      {/* Sub-tab bar. Reserved tabs render disabled with Coming Soon. */}
      <div
        className="bg-white border border-slate-200 rounded-lg p-1 flex items-center gap-1 flex-wrap"
        data-testid="cash-mgmt-subtabs"
      >
        {SUB_TABS.map((t) => {
          const active = subTab === t.id;
          const disabled = !t.active;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => !disabled && setSubTab(t.id)}
              disabled={disabled}
              aria-selected={active}
              data-testid={`cash-mgmt-subtab-${t.id}`}
              className={`
                flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold
                transition-colors border
                ${disabled
                  ? 'bg-slate-50 text-slate-400 border-slate-100 cursor-not-allowed'
                  : active
                    ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                    : 'bg-white text-slate-600 border-transparent hover:text-slate-900 hover:border-slate-200'
                }
              `}
            >
              <t.Icon size={12} />
              <span>{t.label}</span>
              {disabled && (
                <span className="ml-1 text-[9px] font-bold px-1 py-0.5 rounded bg-slate-200 text-slate-500 uppercase tracking-wide">
                  Soon
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Content pane */}
      {!effectiveBranchId ? (
        <div
          className="bg-white border border-dashed border-slate-300 rounded-lg p-8 text-center text-xs text-slate-500 italic"
          data-testid="cash-mgmt-no-branch"
        >
          {branches.length > 1
            ? 'Select a branch above to view cash-management details.'
            : 'Loading branches…'}
        </div>
      ) : loading ? (
        <div className="bg-white border border-slate-200 rounded-lg p-8 text-center text-xs text-slate-500 flex items-center justify-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading snapshot…
        </div>
      ) : err ? (
        <div className="bg-rose-50 border border-rose-200 text-rose-800 text-xs rounded-lg p-3" data-testid="cash-mgmt-error">
          {err}
        </div>
      ) : subTab === 'overview' ? (
        <OverviewPanel position={position} eod={eod} />
      ) : subTab === 'petty' ? (
        <PettyCashPanel
          position={position} date={date} branchId={effectiveBranchId}
          canWrite={!position?.closed}
          onChange={load}
        />
      ) : subTab === 'count' ? (
        <CashCountPanel
          position={position} eod={eod} date={date} branchId={effectiveBranchId}
          canWrite={!position?.closed}
          onChange={load}
        />
      ) : subTab === 'deposits' ? (
        <BankDepositsPanel
          position={position} date={date} branchId={effectiveBranchId}
          canWrite={!position?.closed}
          onChange={load}
        />
      ) : subTab === 'reconciliation' ? (
        <ReconciliationPanel
          position={position} eod={eod} date={date} branchId={effectiveBranchId}
          user={user}
          onChange={load}
        />
      ) : null}

      {/* Architecture footnote — non-interactive; documents the guardrail */}
      <p
        className="text-[10px] italic text-slate-400 leading-relaxed px-1"
        data-testid="cash-mgmt-guardrail-note"
      >
        Bank Deposit does not modify Expected Closing Cash. Cash Remaining
        is Actual Cash minus Bank Deposits, and is only calculated when an
        actual count has been entered. Bank Deposit ≠ Cash Withdrawal ≠
        Petty Cash ≠ Cash Transfer ≠ Accounting Adjustment — each is a
        distinct authorisation surface.
      </p>
    </div>
  );
}

// -------- Overview -------------------------------------------------
// Read-only snapshot. Sources every value from the existing endpoints:
//   opening_cash   ← /reports/eod/cash-position
//   cash_collected ← /reports/eod  (cash_handover)
//   petty_cash     ← /reports/eod/cash-position  (petty_cash_total)
//   expected       ← /reports/eod  (cash_handover.closing_cash)
//   actual_cash    ← /reports/eod/cash-position
//   variance       ← /reports/eod  (cash_handover.variance / .variance_status)
//   bank_deposit   ← /reports/eod/cash-position  (bank_deposit_total)
//   cash_remaining ← /reports/eod/cash-position  (null when no actual)
function OverviewPanel({ position, eod }) {
  const ch = eod?.cash_handover || {};
  const opening = position?.opening_cash ?? ch.opening_cash ?? null;
  const collection = ch.cash_collected ?? 0;
  const petty = position?.petty_cash_total ?? ch.petty_cash ?? 0;
  const expected = ch.closing_cash ?? null;
  const actual = position?.actual_cash ?? ch.actual_cash ?? null;
  const variance = ch.variance ?? null;
  const status = ch.variance_status || null;
  const bank = position?.bank_deposit_total ?? ch.bank_deposit ?? 0;
  const remaining = position?.cash_remaining ?? ch.cash_remaining ?? null;

  const statusStyle = status === 'MATCHED'
    ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
    : status === 'SHORT'
      ? 'bg-rose-50 text-rose-800 border-rose-200'
      : status === 'OVER'
        ? 'bg-amber-50 text-amber-800 border-amber-200'
        : 'bg-slate-50 text-slate-500 border-slate-200 italic';

  return (
    <div
      className="bg-white border border-slate-200 rounded-lg overflow-hidden"
      data-testid="cash-mgmt-overview"
    >
      <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-[11px] font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
        <Wallet size={12} className="text-emerald-600" /> Cash Flow Snapshot
      </div>
      <table className="w-full text-sm">
        <tbody>
          <Row label="Opening Cash"        value={opening == null ? '—' : fmtINR(opening)}    testid="cash-mgmt-opening"      />
          <Row label="Cash Collection"     value={fmtINR(collection)}                          testid="cash-mgmt-collection"   />
          <Row label="Less: Petty Cash"    value={`− ${fmtINR(petty)}`}  minor                testid="cash-mgmt-petty-total"  />
          <Divider />
          <Row label="Expected Closing Cash" value={expected == null ? '—' : fmtINR(expected)} strong testid="cash-mgmt-expected" />
          <Row label="Actual Cash Counted" value={actual == null ? <em className="text-slate-400">Not entered</em> : fmtINR(actual)} testid="cash-mgmt-actual" />
          <Row label="Variance"            value={variance == null ? <em className="text-slate-400">Not calculated</em> : fmtINR(variance)} testid="cash-mgmt-variance" />
          <tr className="border-t border-slate-100">
            <td className="px-4 py-2 text-slate-600 text-xs">Status</td>
            <td className="px-4 py-2 text-right">
              <span
                className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded border ${statusStyle}`}
                data-testid="cash-mgmt-status"
              >
                {status || 'Not entered'}
              </span>
            </td>
          </tr>
          <Divider />
          <Row label="Bank Deposit"        value={`− ${fmtINR(bank)}`}  minor                 testid="cash-mgmt-bank-total"    />
          <Row
            label="Cash Remaining"
            value={remaining == null
              ? <em className="text-slate-400">Not calculated</em>
              : fmtINR(remaining)}
            strong
            testid="cash-mgmt-remaining"
          />
        </tbody>
      </table>
    </div>
  );
}

function Row({ label, value, strong, minor, testid }) {
  return (
    <tr className="border-t border-slate-100" data-testid={testid}>
      <td className={`px-4 py-2 text-xs ${strong ? 'font-bold text-slate-900' : minor ? 'text-rose-700' : 'text-slate-600'}`}>
        {label}
      </td>
      <td className={`px-4 py-2 text-right ${strong ? 'text-base font-bold text-slate-900' : minor ? 'text-rose-700 font-semibold text-sm' : 'text-slate-900 text-sm'}`}>
        {value}
      </td>
    </tr>
  );
}

function Divider() {
  return (
    <tr>
      <td colSpan={2} className="px-0 py-0">
        <div className="border-t border-dashed border-slate-200" />
      </td>
    </tr>
  );
}

// -------- Petty Cash -----------------------------------------------
function PettyCashPanel({ position, date, branchId, canWrite, onChange }) {
  const entries = useMemo(
    () => (position?.outflows || []).filter((o) => o.kind === 'petty_cash'),
    [position],
  );
  const total = Number(position?.petty_cash_total || 0);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const submit = async () => {
    const amt = parseFloat(amount);
    if (Number.isNaN(amt) || amt <= 0) { setMsg('Amount must be greater than zero'); return; }
    if (!reason.trim()) { setMsg('Reason is required'); return; }
    setSaving(true); setMsg('');
    try {
      await axios.post(`${API}/reports/eod/cash-position/outflow`, {
        date, branch_id: branchId, kind: 'petty_cash',
        amount: amt, reason: reason.trim(),
      });
      setAmount(''); setReason(''); setMsg('Added');
      onChange && onChange();
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Failed to add petty cash');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="cash-mgmt-petty">
      <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-[11px] font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
        <Coins size={12} className="text-amber-600" /> Petty Cash · Today {fmtINR(total)}
      </div>
      <div className="p-3 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-[130px_1fr_auto] gap-2">
          <label className="block">
            <span className="block text-[10px] font-semibold uppercase text-slate-500 mb-0.5">Amount (₹)</span>
            <input
              type="number" min="0" step="0.01"
              value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              disabled={!canWrite || saving}
              data-testid="cash-mgmt-petty-amount"
              className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:border-emerald-400 disabled:bg-slate-50"
            />
          </label>
          <label className="block">
            <span className="block text-[10px] font-semibold uppercase text-slate-500 mb-0.5">Reason</span>
            <input
              type="text" value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Auto rickshaw to bank"
              disabled={!canWrite || saving}
              data-testid="cash-mgmt-petty-reason"
              className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:border-emerald-400 disabled:bg-slate-50"
            />
          </label>
          <button
            type="button" onClick={submit}
            disabled={!canWrite || saving}
            data-testid="cash-mgmt-petty-add-btn"
            className="self-end px-4 py-1.5 text-xs font-bold rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? 'Adding…' : 'Add'}
          </button>
        </div>
        {msg && (
          <div className="text-[11px] text-slate-500 italic" data-testid="cash-mgmt-petty-msg">{msg}</div>
        )}
        <OutflowList
          entries={entries}
          empty="No petty cash entries yet."
          emptyTestid="cash-mgmt-petty-empty"
          rowTestid="cash-mgmt-petty-row"
          columns={['Amount', 'Reason', 'Time', 'Recorded By']}
          renderRow={(o) => ([
            fmtINR(o.amount),
            o.reason || '—',
            fmtTime(o.created_at),
            o.actor_user_id || '—',
          ])}
        />
        <div className="border-t border-dashed border-slate-200 pt-2 flex items-center justify-between text-xs">
          <span className="font-semibold text-slate-600">Total Petty Cash Today</span>
          <span className="font-bold text-slate-900" data-testid="cash-mgmt-petty-total">{fmtINR(total)}</span>
        </div>
      </div>
    </div>
  );
}

// -------- Cash Count -----------------------------------------------
function CashCountPanel({ position, eod, date, branchId, canWrite, onChange }) {
  const expected = eod?.cash_handover?.closing_cash;
  const actualPersisted = position?.actual_cash;
  const variance = eod?.cash_handover?.variance;
  const status = eod?.cash_handover?.variance_status;
  const [amount, setAmount] = useState(actualPersisted == null ? '' : String(actualPersisted));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    setAmount(actualPersisted == null ? '' : String(actualPersisted));
    setMsg('');
  }, [actualPersisted, date, branchId]);

  const submit = async () => {
    const n = parseFloat(amount);
    if (Number.isNaN(n) || n < 0) { setMsg('Actual cash must be a non-negative number'); return; }
    setSaving(true); setMsg('');
    try {
      await axios.put(`${API}/reports/eod/cash-position/actual-cash`, {
        date, branch_id: branchId, actual_cash: n,
      });
      setMsg('Saved');
      onChange && onChange();
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const statusStyle = status === 'MATCHED'
    ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
    : status === 'SHORT'
      ? 'bg-rose-50 text-rose-800 border-rose-200'
      : status === 'OVER'
        ? 'bg-amber-50 text-amber-800 border-amber-200'
        : 'bg-slate-50 text-slate-500 border-slate-200 italic';

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="cash-mgmt-count">
      <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-[11px] font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
        <Calculator size={12} className="text-sky-600" /> Cash Count
      </div>
      <div className="p-3 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="border border-slate-200 rounded px-3 py-2 bg-slate-50">
            <div className="text-[10px] uppercase font-semibold text-slate-500 mb-0.5">Expected Closing Cash</div>
            <div className="text-lg font-bold text-slate-900" data-testid="cash-mgmt-count-expected">
              {expected == null ? '—' : fmtINR(expected)}
            </div>
            <div className="text-[10px] italic text-slate-400 mt-1">
              Opening + Cash Collection − Petty Cash
            </div>
          </div>
          <div className="border border-slate-200 rounded px-3 py-2">
            <div className="text-[10px] uppercase font-semibold text-slate-500 mb-0.5">Actual Cash Count</div>
            <div className="flex items-center gap-2">
              <input
                type="number" min="0" step="0.01"
                value={amount} onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                disabled={!canWrite || saving}
                data-testid="cash-mgmt-count-input"
                className="flex-1 text-lg font-bold border border-slate-300 rounded px-2 py-1 focus:outline-none focus:border-emerald-400 disabled:bg-slate-50"
              />
              <button
                type="button" onClick={submit}
                disabled={!canWrite || saving}
                data-testid="cash-mgmt-count-save-btn"
                className="px-4 py-2 text-xs font-bold rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving…' : 'Save Cash Count'}
              </button>
            </div>
            {msg && (
              <div className="text-[11px] text-slate-500 italic mt-1" data-testid="cash-mgmt-count-msg">{msg}</div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-1">
          <StatBlock label="Actual Cash Counted" value={actualPersisted == null ? 'Not entered' : fmtINR(actualPersisted)} testid="cash-mgmt-count-actual" />
          <StatBlock label="Variance" value={variance == null ? 'Not calculated' : fmtINR(variance)} testid="cash-mgmt-count-variance" />
          <div className="border border-slate-200 rounded px-3 py-2">
            <div className="text-[10px] uppercase font-semibold text-slate-500 mb-1">Status</div>
            <span
              className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded border ${statusStyle}`}
              data-testid="cash-mgmt-count-status"
            >
              {status || 'Not entered'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// -------- Bank Deposits --------------------------------------------
function BankDepositsPanel({ position, date, branchId, canWrite, onChange }) {
  const entries = useMemo(
    () => (position?.outflows || []).filter((o) => o.kind === 'bank_deposit'),
    [position],
  );
  const total = Number(position?.bank_deposit_total || 0);
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const submit = async () => {
    const amt = parseFloat(amount);
    if (Number.isNaN(amt) || amt <= 0) { setMsg('Amount must be greater than zero'); return; }
    setSaving(true); setMsg('');
    try {
      await axios.post(`${API}/reports/eod/cash-position/outflow`, {
        date, branch_id: branchId, kind: 'bank_deposit',
        amount: amt,
        reason: reference.trim() || 'Bank deposit',
        reference: reference.trim() || undefined,
      });
      setAmount(''); setReference(''); setMsg('Recorded');
      onChange && onChange();
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Failed to record deposit');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="cash-mgmt-deposits">
      <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-[11px] font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
        <Landmark size={12} className="text-indigo-600" /> Bank Deposit · Today {fmtINR(total)}
      </div>
      <div className="p-3 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-[130px_1fr_auto] gap-2">
          <label className="block">
            <span className="block text-[10px] font-semibold uppercase text-slate-500 mb-0.5">Amount (₹)</span>
            <input
              type="number" min="0" step="0.01"
              value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              disabled={!canWrite || saving}
              data-testid="cash-mgmt-deposit-amount"
              className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:border-emerald-400 disabled:bg-slate-50"
            />
          </label>
          <label className="block">
            <span className="block text-[10px] font-semibold uppercase text-slate-500 mb-0.5">Reference / Note</span>
            <input
              type="text" value={reference} onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. HDFC deposit slip 12345"
              disabled={!canWrite || saving}
              data-testid="cash-mgmt-deposit-reference"
              className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:border-emerald-400 disabled:bg-slate-50"
            />
          </label>
          <button
            type="button" onClick={submit}
            disabled={!canWrite || saving}
            data-testid="cash-mgmt-deposit-record-btn"
            className="self-end px-4 py-1.5 text-xs font-bold rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? 'Recording…' : 'Record Deposit'}
          </button>
        </div>
        {msg && (
          <div className="text-[11px] text-slate-500 italic" data-testid="cash-mgmt-deposit-msg">{msg}</div>
        )}
        <OutflowList
          entries={entries}
          empty="No bank deposits yet."
          emptyTestid="cash-mgmt-deposit-empty"
          rowTestid="cash-mgmt-deposit-row"
          columns={['Amount', 'Reference', 'Time', 'Recorded By']}
          renderRow={(o) => ([
            fmtINR(o.amount),
            o.reference || o.reason || '—',
            fmtTime(o.created_at),
            o.actor_user_id || '—',
          ])}
        />
        <div className="border-t border-dashed border-slate-200 pt-2 flex items-center justify-between text-xs">
          <span className="font-semibold text-slate-600">Total Bank Deposits Today</span>
          <span className="font-bold text-slate-900" data-testid="cash-mgmt-deposit-total">{fmtINR(total)}</span>
        </div>
      </div>
    </div>
  );
}

// -------- shared bits ----------------------------------------------
function StatBlock({ label, value, testid }) {
  return (
    <div className="border border-slate-200 rounded px-3 py-2" data-testid={testid}>
      <div className="text-[10px] uppercase font-semibold text-slate-500 mb-0.5">{label}</div>
      <div className="text-sm font-bold text-slate-900">{value}</div>
    </div>
  );
}

function OutflowList({ entries, empty, emptyTestid, rowTestid, columns, renderRow }) {
  if (!entries || entries.length === 0) {
    return (
      <div className="text-[11px] italic text-slate-400 py-2" data-testid={emptyTestid}>
        {empty}
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-slate-500">
          <tr>
            {columns.map((c) => (
              <th key={c} className="text-left px-2 py-1 font-semibold uppercase tracking-wider text-[10px]">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {entries.map((o) => (
            <tr key={o.outflow_id} className="border-t border-slate-100" data-testid={`${rowTestid}-${o.outflow_id}`}>
              {renderRow(o).map((cell, i) => (
                <td key={i} className="px-2 py-1 text-slate-700">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// -------- Reconciliation -------------------------------------------
// Cash Reconciliation is the "day closure" for Branch Cash Management.
// Success flips the server-side `closed=true` flag on the day's
// cash_positions row — that in turn re-uses the pre-existing 409
// guards on the opening, actual-cash, and outflow endpoints, so the
// day is frozen without any per-endpoint changes.
//
// Roles that can reconcile: front_desk, clinic_owner, super_admin,
// founder, accounts (mirrors backend EOD_ROLES). Roles that can
// reopen: clinic_owner, super_admin, founder (backend _REOPEN_ROLES —
// front_desk and accounts cannot reopen). The server is the
// authoritative gate; the UI merely hides the button.
const REOPEN_ROLES_UI = ['clinic_owner', 'super_admin', 'founder'];

function ReconciliationPanel({ position, eod, date, branchId, user, onChange }) {
  const canReopen = REOPEN_ROLES_UI.includes(user?.role);
  // Server is authoritative for `reconciled` — DO NOT infer from
  // `closed` alone (legacy `closed=true` rows without reconciled_at
  // are explicitly NOT reconciled per Phase 3 clarification §1).
  const reconciled = !!position?.reconciled;
  const legacyClosed = !!position?.closed && !position?.reconciled;

  // Live variance BEFORE the user has reconciled (drives the pill)
  const liveActual = position?.actual_cash;
  const liveVariance = eod?.cash_handover?.variance;
  const liveStatus = eod?.cash_handover?.variance_status;
  // Snapshot AFTER reconciliation (authoritative for the badge)
  const snapVariance = position?.reconciled_variance;
  const snapStatus = position?.reconciled_variance_status;

  const [reconciling, setReconciling] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [reopening, setReopening] = useState(false);
  const [err, setErr] = useState('');

  const reconcile = async () => {
    setReconciling(true); setErr('');
    try {
      await axios.post(`${API}/reports/eod/cash-position/reconcile`, {
        date, branch_id: branchId,
      });
      onChange && onChange();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Reconcile failed');
    } finally {
      setReconciling(false);
    }
  };

  const reopen = async () => {
    if (!reopenReason.trim()) { setErr('Reason is required'); return; }
    setReopening(true); setErr('');
    try {
      await axios.post(`${API}/reports/eod/cash-position/reopen`, {
        date, branch_id: branchId, reason: reopenReason.trim(),
      });
      setReopenOpen(false);
      setReopenReason('');
      onChange && onChange();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Reopen failed');
    } finally {
      setReopening(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="cash-mgmt-recon">
      {/* Snapshot (same rows as Overview, reused for context) */}
      <OverviewPanel position={position} eod={eod} />

      <div
        className="bg-white border border-slate-200 rounded-lg overflow-hidden"
        data-testid="cash-mgmt-recon-status-card"
      >
        <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-[11px] font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
          <ClipboardCheck size={12} className="text-emerald-600" /> Reconciliation Status
        </div>
        <div className="p-4 space-y-3">
          {legacyClosed ? (
            <StatusPill
              tone="slate"
              icon={<AlertTriangle size={14} />}
              label="DAY ALREADY CLOSED — LEGACY CLOSURE"
              detail="This day was closed outside the reconciliation workflow. It cannot be reconciled or reopened here."
              testid="cash-mgmt-recon-status-pill"
            />
          ) : reconciled ? (
            <>
              {snapStatus === 'MATCHED' ? (
                <StatusPill
                  tone="emerald"
                  icon={<CheckCircle2 size={14} />}
                  label="RECONCILED — MATCHED"
                  detail={`Variance ${fmtINR(snapVariance || 0)}`}
                  testid="cash-mgmt-recon-status-pill"
                />
              ) : snapStatus === 'SHORT' ? (
                <StatusPill
                  tone="rose"
                  icon={<AlertTriangle size={14} />}
                  label={`RECONCILED — SHORT ${fmtINR(Math.abs(snapVariance || 0))}`}
                  detail="Reconciled with a cash shortage. No accounting adjustment was created."
                  testid="cash-mgmt-recon-status-pill"
                />
              ) : snapStatus === 'OVER' ? (
                <StatusPill
                  tone="amber"
                  icon={<AlertTriangle size={14} />}
                  label={`RECONCILED — OVER ${fmtINR(snapVariance || 0)}`}
                  detail="Reconciled with a cash overage. No accounting adjustment was created."
                  testid="cash-mgmt-recon-status-pill"
                />
              ) : (
                <StatusPill
                  tone="amber"
                  icon={<AlertTriangle size={14} />}
                  label="RECONCILED — ACTUAL CASH NOT COUNTED"
                  detail="No physical count was entered before reconciliation. Variance was not calculated."
                  testid="cash-mgmt-recon-status-pill"
                />
              )}
              <div className="text-[11px] text-slate-600 space-y-0.5">
                <div>
                  <span className="uppercase font-semibold text-[10px] text-slate-500 tracking-wider">Reconciled by</span>{' '}
                  <span data-testid="cash-mgmt-recon-reconciled-by">{position?.reconciled_by_user_id || '—'}</span>
                </div>
                <div>
                  <span className="uppercase font-semibold text-[10px] text-slate-500 tracking-wider">Reconciled at</span>{' '}
                  <span data-testid="cash-mgmt-recon-reconciled-at">{fmtTime(position?.reconciled_at)}</span>
                </div>
              </div>
              {canReopen && (
                <button
                  type="button"
                  onClick={() => setReopenOpen(true)}
                  data-testid="cash-mgmt-recon-reopen-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                >
                  <RotateCcw size={12} /> Reopen Day
                </button>
              )}
            </>
          ) : (
            <>
              {liveStatus === 'MATCHED' ? (
                <StatusPill
                  tone="emerald"
                  icon={<CheckCircle2 size={14} />}
                  label="READY TO RECONCILE"
                  detail={`Variance ${fmtINR(liveVariance || 0)} — Actual matches Expected.`}
                  testid="cash-mgmt-recon-status-pill"
                />
              ) : liveStatus === 'SHORT' ? (
                <StatusPill
                  tone="rose"
                  icon={<AlertTriangle size={14} />}
                  label={`RECONCILE WITH SHORTAGE ${fmtINR(Math.abs(liveVariance || 0))}`}
                  detail="Reconciling will lock the day. No accounting adjustment is created."
                  testid="cash-mgmt-recon-status-pill"
                />
              ) : liveStatus === 'OVER' ? (
                <StatusPill
                  tone="amber"
                  icon={<AlertTriangle size={14} />}
                  label={`RECONCILE WITH OVERAGE ${fmtINR(liveVariance || 0)}`}
                  detail="Reconciling will lock the day. No accounting adjustment is created."
                  testid="cash-mgmt-recon-status-pill"
                />
              ) : (
                <StatusPill
                  tone="slate"
                  icon={<AlertTriangle size={14} />}
                  label="RECONCILE WITHOUT ACTUAL CASH COUNT"
                  detail="No physical count entered — Variance will be recorded as Not calculated."
                  testid="cash-mgmt-recon-status-pill"
                />
              )}
              <button
                type="button"
                onClick={reconcile}
                disabled={reconciling || position?.opening_cash == null}
                data-testid="cash-mgmt-recon-confirm-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {reconciling ? 'Reconciling…' : 'Confirm & Reconcile'}
              </button>
              {position?.opening_cash == null && (
                <div className="text-[11px] italic text-slate-500">
                  Set Opening Cash before reconciling this day.
                </div>
              )}
            </>
          )}
          {err && (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1" data-testid="cash-mgmt-recon-error">
              {err}
            </div>
          )}
        </div>
      </div>

      {reopenOpen && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
          data-testid="cash-mgmt-recon-reopen-modal"
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="text-sm font-bold text-slate-900">Reopen Reconciled Day</div>
              <button
                type="button"
                onClick={() => { setReopenOpen(false); setReopenReason(''); setErr(''); }}
                data-testid="cash-mgmt-recon-reopen-cancel"
                className="text-slate-400 hover:text-slate-700"
              >
                <X size={16} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="text-[11px] text-slate-600">
                A reason is required and will be recorded in the audit log.
                Cash-management writes will be re-enabled after reopening.
              </div>
              <label className="block">
                <span className="block text-[10px] font-semibold uppercase text-slate-500 mb-1">Reason</span>
                <textarea
                  value={reopenReason}
                  onChange={(e) => setReopenReason(e.target.value)}
                  placeholder="e.g. Bank deposit entered incorrectly"
                  rows={3}
                  disabled={reopening}
                  data-testid="cash-mgmt-recon-reopen-reason"
                  className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:border-emerald-400 disabled:bg-slate-50"
                />
              </label>
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => { setReopenOpen(false); setReopenReason(''); setErr(''); }}
                  disabled={reopening}
                  className="px-3 py-1.5 text-xs font-semibold rounded bg-white border border-slate-300 text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={reopen}
                  disabled={reopening || !reopenReason.trim()}
                  data-testid="cash-mgmt-recon-reopen-confirm"
                  className="px-3 py-1.5 text-xs font-bold rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {reopening ? 'Reopening…' : 'Confirm Reopen'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusPill({ tone, icon, label, detail, testid }) {
  const toneCls = tone === 'emerald'
    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
    : tone === 'rose'
      ? 'bg-rose-50 border-rose-200 text-rose-800'
      : tone === 'amber'
        ? 'bg-amber-50 border-amber-200 text-amber-800'
        : 'bg-slate-50 border-slate-200 text-slate-700';
  return (
    <div className={`border rounded px-3 py-2 ${toneCls}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider">
        {icon}
        <span>{label}</span>
      </div>
      {detail && (
        <div className="text-[11px] mt-0.5 opacity-90">{detail}</div>
      )}
    </div>
  );
}
